import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Upload, FileText } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useMarkingStore } from '../stores/markingStore';
import TaskMarklistAnalysis from '../components/marking/TaskMarklistAnalysis';

const AnalysisPage = () => {
  const { user } = useAuthStore();
  const {
    markingJobs,
    markingTasks,
    markingQuestions,
    loadMarkingJobs,
    loadMarkingTasks,
    loadMarkingQuestions,
    isLoading
  } = useMarkingStore();

  const [sourceMode, setSourceMode] = useState('marked-jobs');
  const [uploadedDataFiles, setUploadedDataFiles] = useState([]);
  const [uploadedScriptFiles, setUploadedScriptFiles] = useState([]);

  useEffect(() => {
    if (!user?.uid) return;
    loadMarkingJobs(user.uid);
    loadMarkingTasks(user.uid);
    loadMarkingQuestions(user.uid);
  }, [user?.uid, loadMarkingJobs, loadMarkingTasks, loadMarkingQuestions]);

  const completedJobs = useMemo(
    () => markingJobs.filter((job) => job.status === 'completed' && job.results),
    [markingJobs]
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-sigma-600 to-sigma-800 p-6">
        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-1.5 text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center">
                <BarChart3 className="w-6 h-6 mr-2" />
                Analysis
              </h1>
              <p className="text-sigma-100 text-sm mt-0.5">
                Task-level marklists and performance analysis
              </p>
            </div>
          </div>
        </div>
        <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/5 rounded-full" />
        <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full" />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Analysis Source</h2>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
          <button
            onClick={() => setSourceMode('marked-jobs')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              sourceMode === 'marked-jobs'
                ? 'bg-white text-sigma-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Marked Jobs
          </button>
          <button
            onClick={() => setSourceMode('upload')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              sourceMode === 'upload'
                ? 'bg-white text-sigma-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Upload Data/Scripts
          </button>
        </div>

        {sourceMode === 'marked-jobs' && (
          <div className="text-xs text-gray-600">
            Using completed marking jobs as the current analysis source.
            {isLoading ? ' Loading...' : ` ${completedJobs.length} completed job(s) available.`}
          </div>
        )}

        {sourceMode === 'upload' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">
              Upload-based analysis workspace is enabled as a first-class entry point. Parsing and merge logic can be expanded next.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50/60 cursor-pointer">
                <div className="text-sm font-medium text-gray-800 inline-flex items-center">
                  <Upload className="w-4 h-4 mr-1.5" /> Upload mark data
                </div>
                <p className="text-xs text-gray-500 mt-1">CSV / JSON / XLSX</p>
                <input
                  type="file"
                  multiple
                  accept=".csv,.json,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setUploadedDataFiles(Array.from(e.target.files || []))}
                />
              </label>
              <label className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50/60 cursor-pointer">
                <div className="text-sm font-medium text-gray-800 inline-flex items-center">
                  <FileText className="w-4 h-4 mr-1.5" /> Upload marked scripts
                </div>
                <p className="text-xs text-gray-500 mt-1">PDF / image files</p>
                <input
                  type="file"
                  multiple
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => setUploadedScriptFiles(Array.from(e.target.files || []))}
                />
              </label>
            </div>
            <div className="text-xs text-gray-500">
              Data files: {uploadedDataFiles.length} | Script files: {uploadedScriptFiles.length}
            </div>
          </div>
        )}
      </div>

      <TaskMarklistAnalysis
        markingJobs={markingJobs}
        markingTasks={markingTasks}
        markingQuestions={markingQuestions}
      />
    </div>
  );
};

export default AnalysisPage;
